import React, { useContext, useEffect, useState } from 'react'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, MenuItem, Select, Switch, TextField, Typography, useMediaQuery } from '@mui/material'
import { API_NS } from 'src/lib/api/endpoints/endpoint.types';
import { adminAction } from 'src/store/actions/admin.actions'
import styled, { useTheme } from 'styled-components'
import { useDispatch } from 'react-redux'
import { useTypedAppSelector } from 'src/store';
import { SnackBarContext } from 'src/contexts/snack-bar.context'

export type proptypes = {
  open: boolean;
  setOpen: (b: boolean) => void;
  isEdit: boolean;
  countries: API_NS.Countries[] | undefined;
  user?: API_NS.ExternalUser | null;
}
export const defaultProps: proptypes = {
  open: false,
  setOpen: () => {},
  isEdit: false,
  countries: []
}

export const _ExternalUserDialog: React.FC<proptypes & { className?: string }> = (props) => {
  const { className, open, setOpen, isEdit, user, countries } = props
  const [extUser, setExtUser] = useState<API_NS.ExternalUser | null>({
    ExternalUserId: undefined,
    UserName: undefined,
    Password: undefined,
    Account: undefined,
    Address: undefined,
    CurrencyCode: undefined,
    CountryCode: undefined,
    isMetric: false,
    Type: undefined,
    NutritionType: undefined,
    BeefFarmer: false,
    HasMycotoxinMapping: false,
    HasFeedout: false,
    Enabled: false
  })
  const [loading, setLoading] = useState<boolean>(false)
  const { setMessage } = useContext(SnackBarContext)
  const dispatch = useDispatch()
  const createStatus = useTypedAppSelector(s => s.adminState.createExternalUserStatus)
  const updateStatus = useTypedAppSelector(s => s.adminState.updateExternalUserStatus)
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'))
  const isDesktop = useMediaQuery(useTheme().breakpoints.up('md'))
  const UserTypes = ['Customer', 'Advisor']
  const NutritionTypes = ['Nutrition Dairy', 'Nutrition Beef']

  const textfield = (placeholder, value, onChange) => <div className='textfield-container'>
    <Typography style={{ fontSize: 13, fontWeight: 500 }}>{placeholder}</Typography>
    <TextField
      variant='outlined'
      size='small'
      inputProps={{ style: { padding: 8, fontSize: 13 } }}
      value={value}
      onChange={onChange}
    />
  </div>

  const addExternalUser = () => {
    if (!extUser?.UserName) return
    
    const user = {...extUser, ExternalUserId: extUser?.ExternalUserId || 0, Country: undefined}
    isEdit ? dispatch(adminAction.updateExternalUser(user)) : dispatch(adminAction.createExternalUser(user))
  }

  const changeInputHandler = (name: string, value: string) => {
    setExtUser((prevData: any) => ({
      ...prevData,
      [name]: value,
    }))
  }

  useEffect(() => {
    user && setExtUser(user)
  }, [user])

  useEffect(() => {
    setLoading(createStatus?.loading || updateStatus?.loading ? true : false)
  }, [createStatus?.loading, updateStatus?.loading])

  useEffect(() => {
    createStatus?.message && setMessage({ message: createStatus.message.msg, severity: createStatus.message.level, clear: () => dispatch(adminAction.clearCreateExternalUserMessage()) });
    updateStatus?.message && setMessage({ message: updateStatus.message.msg, severity: updateStatus.message.level, clear: () => dispatch(adminAction.clearUpdateExternalUserMessage()) });
    (createStatus?.message?.level === 'success' || updateStatus?.message?.level === 'success') && setOpen(false);
  }, [createStatus?.message, updateStatus?.message])

  useEffect(() => {
    return () => {
      setExtUser(null)
      dispatch(adminAction.clearExternalUser())
    }
  }, [])

  return <Dialog className={className} fullWidth maxWidth={isMobile ? 'sm' : isDesktop ? 'md' : 'md'} open={open} onClose={() => setOpen(false)}>
    <DialogTitle>{loading ? '' : 'Add New External User'}</DialogTitle>
    <DialogContent style={{ display: 'flex', flexDirection: 'column', height: 500 }}>
      {loading ? <div className='loading-screen'><CircularProgress size={40} /></div>
      : <div className='dialog-content'>
        {textfield('ID', extUser?.ExternalUserId || '', e => changeInputHandler('ExternalUserId', e.target.value) )}
        {textfield('Username', extUser?.UserName || '', e => changeInputHandler('UserName', e.target.value) )}
        {textfield('Password', extUser?.Password || '', e => changeInputHandler('Password', e.target.value) )}
        {textfield('Account', extUser?.Account || '', e => changeInputHandler('Account', e.target.value) )}
        {textfield('Address', extUser?.Address || '', e => changeInputHandler('Address', e.target.value) )}
       
        <FormControl sx={{ minWidth: 120 }} size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>Country</Typography>
          <Select color='secondary' style={{ fontSize: 13 }} value={extUser?.CountryCode || ''}>
            {(countries || []).map((c, i) => <MenuItem 
              key={i}
              style={{ fontSize: 14 }}
              value={c?.LanguageCode || ''}
              selected={c?.LanguageCode === extUser?.CountryCode}
              onClick={() => setExtUser((prevData: any) => ({ ...prevData, CountryCode: c?.LanguageCode }))}>
              {c?.CountryName}
            </MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }} size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>Type</Typography>
          <Select color='secondary' style={{ fontSize: 13 }} value={extUser?.Type || 'Customer'}>
            {UserTypes.map((u, i) => <MenuItem 
              key={i}
              style={{ fontSize: 14 }}
              value={u}
              selected={u === extUser?.Type}
              onClick={() => setExtUser((prevData: any) => ({ ...prevData, Type: u }))}>
              {u}
            </MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }} size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>Nutrition Type</Typography>
          <Select color='secondary' style={{ fontSize: 13 }} value={extUser?.NutritionType || 'Nutrition Dairy'}>
            {NutritionTypes.map((n, i) => <MenuItem 
              key={i}
              style={{ fontSize: 14 }}
              value={n}
              selected={n === extUser?.NutritionType}
              onClick={() => setExtUser((prevData: any) => ({ ...prevData, NutritionType: n }))}>
              {n}
            </MenuItem>)}
          </Select>
        </FormControl>

        <FormControl className='switch-container' size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>isMetric</Typography>
          <Switch
            color='primary'
            checked={Boolean(extUser?.isMetric)}
            onChange={e => setExtUser((prevData: any) => ({ ...prevData, isMetric: e.target.checked}))}
          />
        </FormControl>

        <FormControl className='switch-container' size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>Beef Farmer?</Typography>
          <Switch
            color='primary'
            checked={Boolean(extUser?.BeefFarmer)}
            onChange={e => setExtUser((prevData: any) => ({ ...prevData, BeefFarmer: e.target.checked}))}
          />
        </FormControl>

        <FormControl className='switch-container' size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>Has Mycotoxin Mapping?</Typography>
          <Switch
            color='primary'
            checked={Boolean(extUser?.HasMycotoxinMapping)}
            onChange={e => setExtUser((prevData: any) => ({ ...prevData, HasMycotoxinMapping: e.target.checked}))}
          />
        </FormControl>

        <FormControl className='switch-container' size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>Has Feedout?</Typography>
          <Switch
            color='primary'
            checked={Boolean(extUser?.HasFeedout)}
            onChange={e => setExtUser((prevData: any) => ({ ...prevData, HasFeedout: e.target.checked}))}
          />
        </FormControl>

        <FormControl className='switch-container' size='small'>
          <Typography style={{ fontSize: 13, fontWeight: 500 }}>Enabled?</Typography>
          <Switch
            color='primary'
            checked={Boolean(extUser?.Enabled)}
            onChange={e => setExtUser((prevData: any) => ({ ...prevData, Enabled: e.target.checked}))}
          />
        </FormControl>
      </div>}
    </DialogContent>
    <DialogActions>
      <Button
        size='small'
        onClick={addExternalUser}
        disabled={!extUser?.ExternalUserId || !extUser?.UserName || !extUser?.Password || !extUser?.Account || !extUser?.Address || !extUser?.CountryCode}
      >
        {isEdit? 'Update User' : 'Add User'}
      </Button>
      <Button size='small' onClick={() => setOpen(false)}>Cancel</Button>
    </DialogActions>
  </Dialog>
}

export const ExternalUserDialog = styled(_ExternalUserDialog)`
  .loading-screen {
    display: flex;
    justify-content: center;
    width: 100%;
  }
  .dialog-content {
    display: grid;
    ${props => props.theme.breakpoints.down('md')} {
      grid-template-columns: 1fr;
    }
    ${props => props.theme.breakpoints.up('md')} {
      grid-template-columns: 1fr 1fr;
      column-gap: 14px;
    }
    row-gap: 10px;
    padding: 8px 0;
    .switch-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      min-width: 120px;
      gap: 12px;
    }
  }
  .textfield-container {
    display: flex;
    flex-direction: column;
  }
`

export default ExternalUserDialog